import moment, { isMoment } from 'moment';
import Big from 'big.js';
import {
    alertType,
    directDebitAllowedCountries,
    entityKey,
    environments,
    EURO_SYMBOL,
    exceptionTierOptions,
    investorErrors,
    paymentMethods,
    ribErrors,
    roleCategories,
    RoleNames,
    stepIDs,
    stepStatus,
    subscriptionStatus,
} from './constants';
import { texts } from './languages/fr';
import { getEntityIdFromIRI } from 'utils/helpers/iri';
import { closingSorter } from 'utils/helpers/sorting';
import { countries } from './formData';

export const formatIBAN = value => {
    if (!value) {
        return '';
    }

    return value
        .toUpperCase()
        .replace(/\s/g, '') // remove whitespaces
        .replace(/(.{4})/g, '$1 ') // divide in groups of 4
        .trim();
};

export const canOperateWithSEPA = bankAccountNumber => {
    if (!bankAccountNumber || bankAccountNumber.length < 15) {
        return false;
    }
    return directDebitAllowedCountries.includes(bankAccountNumber.slice(0, 2).toUpperCase());
};

export const formatIBANOnType = element => {
    const countSpaces = text => {
        const spaces = text.match(/(\s+)/g);
        return spaces ? spaces.length : 0;
    };

    const position = element.selectionEnd;
    const previousValue = element.value;
    element.value = formatIBAN(element.value);

    if (position !== element.value.length) {
        const beforeCaret = previousValue.substr(0, position);
        const countPrevious = countSpaces(beforeCaret);
        const countCurrent = countSpaces(formatIBAN(beforeCaret));
        element.selectionEnd = position + (countCurrent - countPrevious);
    }

    return element.value;
};

export const loadScript = (src, target, id) =>
    new Promise(resolve => {
        if (!target) {
            return;
        }

        const script = document.createElement('script');
        script.setAttribute('async', false);
        script.setAttribute('id', id);
        script.setAttribute('src', src);
        target.appendChild(script);
        script.addEventListener('load', resolve, { once: true });
    });

// matches is an array of indexes corresponding to parts of the text that matched google places autocomplete input
// matches = [[startIndex0, endIndex0], [startIndex1, endIndex1], ...]
export const parseMatches = (text, matches) => {
    let result = [];

    if (matches.length === 0) {
        // if there are no matches, all text is not highlighted
        result.push({
            text: text,
            highlight: false,
        });
    } else if (matches[0][0] > 0) {
        // if the first match does not start at the beginning of the text
        // [0, startIndex0] is not highlighted
        result.push({
            text: text.slice(0, matches[0][0]),
            highlight: false,
        });
    }

    matches.forEach((match, i) => {
        let startIndex = match[0];
        let endIndex = match[1];

        result.push({
            text: text.slice(startIndex, endIndex),
            highlight: true,
        });

        if (i === matches.length - 1) {
            if (endIndex < text.length) {
                // if the last match ends before the text ending
                // [endIndex_lastMatch, endText] is not highlighted
                result.push({
                    text: text.slice(endIndex, text.length),
                    highlight: false,
                });
            }
        } else if (endIndex < matches[i + 1][0]) {
            // if there is a gap between two consecutive matches
            // [endIndex_i, startIndex_i+1] is not highlighted
            result.push({
                text: text.slice(endIndex, matches[i + 1][0]),
                highlight: false,
            });
        }
    });

    return result;
};

export const parseDate = isoDate => {
    const timestamp = Date.parse(isoDate);
    if (!isNaN(timestamp)) {
        const date = new Date(isoDate);
        return date.toLocaleDateString('fr-FR');
    }
    return null;
};

export const arrayLodashCustomizer = (objValue, srcValue, key) => {
    if (Array.isArray(srcValue) && key !== 'subscriptions') {
        return [...srcValue];
    } else if (key === 'subscriptions' && srcValue?.length > objValue?.length) {
        // a subscription was deleted
        return [...objValue];
    }
    if (objValue !== null && typeof objValue === 'object' && typeof srcValue !== 'object')
        return objValue;
    return srcValue === null || typeof srcValue === 'undefined' ? objValue : undefined;
};

export const parseNumber = (number, decimals = 2, alwaysShowDecimals = false) => {
    if (isNaN(number) || !number) return '0';
    let hasDecimals = alwaysShowDecimals ? true : Math.round(number) !== number;
    const result = parseFloat(number).toLocaleString('fr', {
        minimumFractionDigits: hasDecimals ? decimals : 0,
        maximumFractionDigits: hasDecimals ? decimals : 0,
    });
    return result;
};

export const parseSum = sum => {
    return parseNumber(sum) + EURO_SYMBOL;
};

export const parseInvestmentFundName = fund => {
    if (fund) {
        const { type, name } = fund;
        const nbsp = String.fromCharCode(160);
        const formattedName = name.split(' ').join(nbsp);
        const formattedType = type.split(' ').join(nbsp);
        return `${formattedType}${nbsp}-${nbsp}${formattedName}`;
    }
    return '';
};

export const checkFormDataHasFiles = formData =>
    formData.entries().next().value.includes('files[]');

export const initJsonField = (json, defaultValue, otherValue) => {
    let field = '';
    let otherField = '';
    try {
        const obj = JSON.parse(json) || {};
        field = obj.choice || defaultValue;
        otherField = obj.other || '';
        return [field, otherField];
    } catch {
        // json is invalid
        if (json) {
            field = otherValue;
            otherField = json;
        } else {
            field = defaultValue;
        }
        return [field, otherField];
    }
};

export const ucFirst = payload => {
    if (!payload) return '';
    let value = payload
        .split(' ')
        .map(str => str.charAt(0).toUpperCase() + str.substring(1).toLowerCase())
        .join(' ');

    if (value.includes('-')) {
        value = value
            .split('-')
            .map(str => {
                if (str.charAt(0) !== str.charAt(0).toUpperCase()) {
                    return str.charAt(0).toUpperCase() + str.substring(1).toLowerCase();
                }
                return str;
            })
            .join('-');
    }

    return value;
};

export const updateArrayEntryById = (array, entry, idKey = 'id') => {
    const index = array.findIndex(x => x[idKey] === entry[idKey]);
    return [...array.slice(0, index), entry, ...array.slice(index + 1)];
};

export const parsePercentage = p => parseFloat(p.slice(0, p.indexOf('.') + 3));

export const jsonParseSafe = (value, defaultValue = null) => {
    try {
        if (value === null) return defaultValue;
        return JSON.parse(value);
    } catch (error) {
        return defaultValue;
    }
};

export const getInProgressSubscription = user =>
    user?.subscriptions?.find(sub => sub.subscriptionStatus === subscriptionStatus.IN_PROGRESS);

// block navigation for distributor if step before last step is verified
export const blockDistributorNavigation = internalUser => {
    const inProgressSubscription = getInProgressSubscription(internalUser);
    const step = inProgressSubscription?.userTunnelSteps?.find(
        step => step.tunnelStep.step.id === stepIDs.DISTRIBUTOR_DOCUMENTS
    );

    return step ? step.status === stepStatus.VERIFIED : false;
};

export const parseFullName = user => {
    if (user?.fullName) return user.fullName;
    if (user) {
        return `${ucFirst(user.firstName)} ${user.lastName?.toUpperCase()}`;
    }
    return '-';
};

export const parseBackendViolations = data => {
    if (data?.violations) {
        const fieldsError = {};
        data.violations.forEach(e => (fieldsError[e['propertyPath']] = e.message));
        return fieldsError;
    }
    return null;
};

export const getInvestorErrorMessage = errorKey => {
    switch (errorKey) {
        case investorErrors.INVALID_NIF:
            return texts.INVALID_NIF_MESSAGE;
        case investorErrors.CO_SUBSCRIBER_INVALID_NIF:
            return texts.INVALID_NIF_MESSAGE;
        case investorErrors.INVALID_IBAN:
            return texts.INVALID_IBAN_MESSAGE;
        case ribErrors.EMPTY_BIC:
            return texts.EMPTY_BIC_MESSAGE;
        case ribErrors.BIC_INVALID:
            return texts.BIC_INVALID;
        case ribErrors.DIRECT_DEBIT_NOT_ALLOWED:
            return texts.DIRECT_DEBIT_NOT_ALLOWED;
        default:
            return '';
    }
};

export const getAlertTypeForUser = subscription => {
    let isWarningAlert = false;
    for (const step of subscription?.userTunnelSteps || []) {
        for (const alert of step.alerts) {
            if (alert.type === alertType.CRITICAL && !alert.solved) {
                return alertType.CRITICAL;
            } else if (alert.type === alertType.WARNING && !alert.solved) {
                isWarningAlert = true;
            }
        }
    }

    return isWarningAlert ? alertType.WARNING : '';
};

export const extractSectionAndQuestion = key => {
    const parts = key.split('.');
    const section = parts.find(p => p.includes('group'));
    const question = parts.find(p => p.includes('question'));
    return { section, question };
};

export const parseKnowledgeLevels = knowledgeLevels => {
    const regexKnowledgeLevels = /Level_1: (?<level_1>-?\d+), Level_2: (?<level_2>-?\d+)/;
    const match = regexKnowledgeLevels.exec(knowledgeLevels);
    return [match.groups.level_1, match.groups.level_2];
};

export const parseSlpKnowledgeLevels = (slpKnowledgeLevels, level) => {
    const regexKnowledgeLevel1 = /Level_1: (?<level_1>-?\d+)/;
    const regexKnowledgeLevel2 = /Level_2: (?<level_2>-?\d+)/;
    const match =
        1 === level
            ? regexKnowledgeLevel1.exec(slpKnowledgeLevels)
            : regexKnowledgeLevel2.exec(slpKnowledgeLevels);
    return match.groups.level_1 || match.groups.level_2;
};

export const parseChoice = (choice, other, messages) => {
    if (choice !== 'general_options.other_2' && choice !== 'general_options.other') {
        return messages[choice];
    } else {
        return `Autre - ${other}`;
    }
};

export const boolToStr = (boolVal, trueValue = 'true', falseValue = 'false') => {
    if (typeof boolVal !== 'boolean') {
        return '';
    }
    return boolVal ? trueValue : falseValue;
};

export const convertToBoolOrNull = (val, trueValue = 'true', falseValue = 'false') => {
    if (typeof val === 'boolean') {
        return val;
    } else if (typeof val === 'string') {
        if (val === trueValue) {
            return true;
        } else if (val === falseValue) {
            return false;
        }
        return null;
    }
    return null;
};

export const convertToBoolOrUndefined = (val, trueValue = 'true', falseValue = 'false') => {
    if (typeof val === 'boolean') {
        return val;
    } else if (typeof val === 'string') {
        if (val === trueValue) {
            return true;
        } else if (val === falseValue) {
            return false;
        }
    }
};

export const getSubscriptionRoleName = currentSubscription => {
    return Object.values(RoleNames).find(value => value === currentSubscription?.role?.name);
};

// arrayToFill can be larger than referenceArray
export const fillArrayToMatchSize = (arrayToFill, referenceArray, fillingObject) => {
    let res = [...arrayToFill];
    if (arrayToFill.length < referenceArray.length) {
        const lengthDifference = referenceArray.length - arrayToFill.length;
        res = [...arrayToFill, ...Array.from({ length: lengthDifference }, () => fillingObject)];
    }
    return res;
};

// this method replaces last space with a unbreakable space character
export const formatLabel = label => {
    const lastSpacePos = label.lastIndexOf(' ');
    return label.substring(0, lastSpacePos) + '\xa0' + label.substring(lastSpacePos + 1);
};

export const getRenderRoleName = role => {
    if (!role) {
        return null;
    }
    switch (role.name) {
        case RoleNames.ADMIN_CIF:
            return texts.ADMIN;
        case RoleNames.COMMERCIALISATEUR:
            return texts.CONSULTANT;
        case RoleNames.NETWORK_ADMIN:
            return texts.NETWORK_ADMIN;
        case RoleNames.PROMOTER_ADMIN:
            return texts.ADMIN;
        case RoleNames.PROMOTER_STANDARD:
            return texts.STANDARD;
        case RoleNames.PROVIDER_STANDARD:
            return texts.PROVIDER_STANDARD;
        case RoleNames.PROVIDER_SIGNATORY:
            return texts.PROVIDER_SIGNATORY;
        case RoleNames.PROVIDER_ADMIN:
            return texts.PROVIDER_ADMIN;
        default:
            return role.name;
    }
};

export const parseReceivedNotifications = (notifications, withFrom) =>
    notifications?.map(notification => {
        let title = `${moment(notification.createdAt).format('L')} - ${notification.subject}`;
        if (withFrom) {
            let company = 'Peqan';
            const distributorCompany = notification.sender?.subscriptions?.[0]?.distributorCompany;
            if (distributorCompany) {
                company = distributorCompany?.corporateName;
            }
            title = `${company} - ${title}`;
        }
        const isHtml =
            /<!DOCTYPE html>/i.test(notification.content) ||
            /<html/i.test(notification.content) ||
            /<head/i.test(notification.content) ||
            /<body/i.test(notification.content);

        return {
            id: notification.id,
            title,
            content: notification.content,
            type: isHtml ? 'html' : 'wysiwyg',
        };
    }) || [];

export const getMessageByFundType = (
    messages,
    key,
    isFPS = false,
    isSLP = false,
    isFPCI = false
) => {
    const keySuffix = isFPS ? '_fps' : isSLP ? '_slp' : isFPCI ? '_fpci' : '';
    return messages.hasOwnProperty(key + keySuffix) ? messages[key + keySuffix] : messages[key];
};

export const is400StatusHandled = response => {
    if (
        ((response?.config?.url?.includes('register/3') ||
            response?.config?.url?.includes('register/46')) &&
            response?.data?.errors?.[0]?.message?.includes(
                investorErrors.CONTACT_INFO_US_PERSON
            )) ||
        response?.config?.url?.includes('networks') ||
        (response?.config?.url?.includes('register/14') &&
            (response?.data?.errors?.[0]?.message?.includes(investorErrors.INVALID_IBAN) ||
                response?.data?.errors?.[0]?.message?.includes(investorErrors.INVALID_NIF)))
    ) {
        return true;
    }

    if (
        response?.config?.url?.includes('register/34') &&
        (response?.data?.errors?.[0]?.message === investorErrors.ELIGIBILITY_INVALID ||
            response?.data?.errors?.[0]?.message === investorErrors.SUM_INVALID ||
            response?.data?.errors?.[0]?.message === investorErrors.ELIGIBILITY_INITIAL_INVALID)
    ) {
        return true;
    }

    if (
        response?.config?.url?.includes('register/41') &&
        response?.data?.errors?.[0]?.message === investorErrors.SUM_INVALID
    ) {
        return true;
    }

    if (
        response?.config?.url?.includes('referrals/invite') &&
        response?.data?.detail === investorErrors.SUBSCRIPTION_IN_PROGRESS
    ) {
        return true;
    }

    if (
        response?.config?.url?.includes('subscription_alerts/') &&
        response?.config?.method === 'patch'
    ) {
        return true;
    }

    if (response?.config?.url?.includes('/subscriptions/external/servicer')) {
        return true;
    }

    if (
        response?.config?.url?.includes('investment_funds') &&
        (response?.config?.method === 'patch' || response?.config?.method === 'post')
    ) {
        return true;
    }

    return (
        response?.config?.url?.includes('distributor_companies') &&
        (response?.config?.method === 'patch' || response?.config?.method === 'post')
    );
};

export const getTermsAndConditionsURL = () => {
    let env = '';
    switch (process.env.REACT_APP_ENV) {
        case environments.TEST:
            env = 'test';
            break;
        case environments.PREPROD:
            env = 'preprod';
            break;
        case environments.PROD:
            env = 'prod';
            break;
        default:
            env = 'dev';
    }

    return `https://ho-pu-backend-${env}.s3.eu-west-3.amazonaws.com/CGU_Plateforme_PE_Tech_102022.pdf`;
};

export const getFundShareCategories = (fundId, categories = []) =>
    categories.filter(
        c => getEntityIdFromIRI(c.investmentFund, entityKey.INVESTMENT_FUND) === fundId
    );

export const getCustomMinInvestment = (investorQualification, subcategories = []) => {
    if (exceptionTierOptions.includes(investorQualification)) return 30000;

    const min = subcategories
        .filter(sc => sc.accessibleToDistribution && sc.minInvestedAmount)
        .map(sc => sc.minInvestedAmount);

    return Math.min(...min);
};

export const getPaginationData = response => {
    const paginationMetadata = response?.headers?.['pagination-metadata'] || '';
    const paginationParts = paginationMetadata.split('/');
    const currentPage = +paginationParts?.[0] || null;
    const lastPage = +paginationParts?.[1] || null;

    return { currentPage, lastPage };
};

export const deviceIsMobile = () => /Mobi|Android/i.test(navigator.userAgent);

export const addBusinessDays = (originalDate, numDaysToAdd, excludeSatrudays) => {
    const Sunday = 0;
    const Saturday = 6;
    let daysRemaining = numDaysToAdd;

    const newDate = isMoment(originalDate) ? originalDate.clone() : moment(originalDate).clone();

    while (daysRemaining > 0) {
        newDate.add(1, 'days');
        if (newDate.day() !== Sunday && (excludeSatrudays ? newDate.day() !== Saturday : true)) {
            daysRemaining--;
        }
    }

    return newDate;
};

export const subtractBusinessDays = (originalDate, numDaysToAdd, excludeSatrudays) => {
    const Sunday = 0;
    const Saturday = 6;
    let daysRemaining = numDaysToAdd;

    const newDate = isMoment(originalDate) ? originalDate.clone() : moment(originalDate).clone();

    while (daysRemaining > 0) {
        newDate.subtract(1, 'days');
        if (newDate.day() !== Sunday && (excludeSatrudays ? newDate.day() !== Saturday : true)) {
            daysRemaining--;
        }
    }

    return newDate;
};

export const getPartialPaymentsTotal = payment => {
    const partials = jsonParseSafe(payment?.partialPayments, []);
    return partials.reduce((acc, crt) => acc.plus(crt.amount), new Big(0)).toNumber();
};

export const getLatestClosing = list => list.sort(closingSorter)?.[0];

export const getFormattedAddress = address => {
    const selectedCountry = countries.find(country => country.id === address.country)?.name || '';
    return `${address.address}, ${address.zipCode ? address.zipCode : ''}${
        address.city ? ` ${address.city}, ` : ''
    }${selectedCountry}`;
};

export const purgeEmptyFields = (obj, purgedValue) => {
    const result = {};
    Object.keys(obj).forEach(key => {
        if (typeof purgedValue !== 'undefined') {
            if (obj[key] !== purgedValue) {
                result[key] = obj[key];
            }
        } else if (obj[key] || typeof obj[key] === 'boolean') {
            result[key] = obj[key];
        }
    });

    return result;
};

export const getButtonLoaderSize = (size = 'large') => {
    switch (size) {
        case 'small':
            return 15;
        case 'medium':
            return 18;
        case 'large':
            return 22;
        default:
            return 20;
    }
};

export const parsePaymentNumber = (type, number, closingComment) => {
    if (type === 'fundraising') {
        return texts.FUND_CALL(number);
    }
    if (type === 'closing') {
        return texts.CLOSING_NUMBER(closingComment);
    }

    return texts.FUND_CALL(number);
};

export const getProfileByRoles = (savedRoles, ppRoles, pmRoles) => {
    if (!savedRoles || !savedRoles.length) return 'all';
    if (savedRoles.every(sr => ppRoles.find(x => x.id === sr))) {
        return 'PP';
    }
    if (savedRoles.every(sr => pmRoles.find(x => x.id === sr))) {
        return 'PM';
    }
    return 'all';
};

export const parseRoleByProfile = (savedRole, allRoles) => {
    switch (savedRole) {
        case 'PP':
            return allRoles.filter(r => r.name.includes('PP')).map(r => r.id);
        case 'PM':
            return allRoles.filter(r => r.name.includes('PM')).map(r => r.id);
        default:
            return allRoles
                .filter(r => r.category?.name === roleCategories.INVESTORS)
                .map(r => r.id);
    }
};

// Create a lookup map for efficient category finding
export const createCategoryLookup = categories => {
    const lookup = new Map();
    categories.forEach(category => {
        category.shareSubcategories?.forEach(subcategory => {
            lookup.set(subcategory.id, category);
        });
    });
    return lookup;
};

export function getPaymentMethodText(paymentMethod) {
    if (paymentMethod === paymentMethods.CREDIT_TRANSFER) return texts.CREDIT_TRANSFER;
    if (paymentMethod === paymentMethods.DIRECT_DEBIT) return texts.DIRECT_DEBIT;
    return '-';
}

export function compileFullAddress({ address = '', city = '', zipCode = '', country = '' }) {
    const selectedCountry = countries.find(c => c.id === country)?.name || '';
    const selectedCity = city ? ` ${city}, ` : '';
    const selectedZipCode = zipCode ? `${zipCode} ` : '';
    return `${address}, ${selectedZipCode}${selectedCity}${selectedCountry}`;
}
